/*
 * LCP 2026-09-29 — product gallery loading and desktop arrows.
 *
 * snippets/product-media-gallery.liquid renders off-screen gallery images
 * without src (data-lz-src / data-lz-srcset): slides 2+ and thumbnails 5+.
 * Chrome's native loading="lazy" still fetched ~10 of them, because it
 * preloads anything within ~1250px of the viewport, even inside a scroller.
 * This file attaches them on demand:
 *   - thumbnails when they scroll into their own list (vertical on desktop,
 *     horizontal on mobile);
 *   - mobile slides as the swipe approaches them, starting only after the
 *     page's load event so slide 2 never competes with the LCP image;
 *   - any slide or thumbnail the moment it becomes active (thumbnail click,
 *     arrows, variant change), so a selected image is never blank;
 *   - popup content (snippets/lazy-rte.liquid, defer: true) when the dialog
 *     gets its open attribute.
 * media-gallery.js is untouched; this only reacts to the is-active class and
 * aria-current it sets. Desktop arrows step through the thumbnails by
 * clicking them, so zoom, variant media and scroll behaviour stay Dawn's.
 */
(function () {
  'use strict';

  var SRC = 'data-lz-src';
  var SRCSET = 'data-lz-srcset';
  var DEFERRED = 'img[' + SRC + ']';

  function attach(img) {
    if (!img || !img.hasAttribute(SRC)) return;
    var srcset = img.getAttribute(SRCSET);
    if (srcset) {
      img.setAttribute('srcset', srcset);
      img.removeAttribute(SRCSET);
    }
    img.setAttribute('src', img.getAttribute(SRC));
    img.removeAttribute(SRC);
  }

  function attachWithin(el) {
    if (!el || el.nodeType !== 1) return;
    if (el.matches(DEFERRED)) attach(el);
    el.querySelectorAll(DEFERRED).forEach(attach);
  }

  function afterLoad(fn) {
    function idle() {
      if ('requestIdleCallback' in window) window.requestIdleCallback(fn, { timeout: 2000 });
      else setTimeout(fn, 200);
    }
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }

  function isVertical(list) {
    return list && window.getComputedStyle(list).flexDirection === 'column';
  }

  /** Keep the active thumbnail visible in the vertical desktop column
   * (media-gallery.js only knows how to scroll horizontally). */
  function revealThumbnail(button) {
    var list = button.closest('.thumbnail-list');
    var item = button.closest('li');
    if (!item || !isVertical(list)) return;
    var top = item.offsetTop;
    var bottom = top + item.offsetHeight;
    if (top >= list.scrollTop && bottom <= list.scrollTop + list.clientHeight) return;
    list.scrollTo({ top: top - (list.clientHeight - item.offsetHeight) / 2, behavior: 'smooth' });
  }

  function onIntersect(entries, observer) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      attach(entry.target);
      observer.unobserve(entry.target);
    });
  }

  var hasIO = 'IntersectionObserver' in window;
  var pageLoaded = false;

  function setup(gallery) {
    var thumbs = gallery.querySelector('.thumbnail-list');
    var slides = gallery.querySelector('.product__media-list');

    // The active slide and thumbnail are always shown.
    attachWithin(gallery.querySelector('.product__media-item.is-active'));
    attachWithin(gallery.querySelector('.thumbnail[aria-current]'));

    if (!hasIO) {
      afterLoad(function () {
        attachWithin(gallery);
      });
      return;
    }

    if (thumbs) {
      gallery._lzThumbs =
        gallery._lzThumbs || new IntersectionObserver(onIntersect, { root: thumbs, rootMargin: '40px' });
      thumbs.querySelectorAll(DEFERRED).forEach(function (img) {
        gallery._lzThumbs.observe(img);
      });
    }

    if (slides && pageLoaded) {
      // Mobile swipe: attach the next slide before it scrolls in. On desktop
      // inactive slides are display:none and never intersect; they load
      // when activated.
      gallery._lzSlides =
        gallery._lzSlides || new IntersectionObserver(onIntersect, { root: slides, rootMargin: '0px 60% 0px 60%' });
      slides.querySelectorAll(DEFERRED).forEach(function (img) {
        gallery._lzSlides.observe(img);
      });
    }
  }

  function setupAll() {
    document.querySelectorAll('media-gallery').forEach(setup);
  }

  // Selection changes: is-active on slides, aria-current on thumbnails.
  // Re-renders (variant change swaps gallery markup): set up the new nodes.
  var pending = null;
  new MutationObserver(function (mutations) {
    var rescan = false;
    mutations.forEach(function (m) {
      if (m.type === 'attributes') {
        var t = m.target;
        if (m.attributeName === 'open') {
          if (t.hasAttribute('open')) attachWithin(t);
        } else if (m.attributeName === 'class' && t.classList.contains('is-active') && t.closest('media-gallery')) {
          attachWithin(t);
        } else if (m.attributeName === 'aria-current' && t.hasAttribute('aria-current') && t.classList.contains('thumbnail')) {
          attachWithin(t);
          revealThumbnail(t);
        }
      } else if (!rescan && m.addedNodes.length) {
        rescan =
          (m.target.closest && m.target.closest('media-gallery')) ||
          Array.prototype.some.call(m.addedNodes, function (n) {
            return n.nodeType === 1 && (n.matches('media-gallery') || n.querySelector('media-gallery'));
          });
      }
    });
    if (rescan && !pending) {
      pending = setTimeout(function () {
        pending = null;
        setupAll();
      }, 50);
    }
  }).observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['class', 'aria-current', 'open'],
  });

  // Desktop arrows over the main image: step to the neighbouring thumbnail.
  document.addEventListener('click', function (event) {
    var arrow = event.target.closest && event.target.closest('[data-lcp-step]');
    if (!arrow) return;
    var gallery = arrow.closest('media-gallery');
    if (!gallery) return;
    var items = Array.prototype.filter.call(gallery.querySelectorAll('.thumbnail-list__item'), function (li) {
      return li.offsetParent !== null;
    });
    if (items.length < 2) return;
    // Position comes from the slide on screen, not aria-current: Dawn's
    // debounced slideChanged (fired on resize) can move aria-current back to
    // the first thumbnail while another slide is shown.
    var active = gallery.querySelector('.product__media-item.is-active');
    var activeId = active && active.getAttribute('data-media-id');
    var current = -1;
    items.forEach(function (li, i) {
      if (activeId && li.getAttribute('data-target') === activeId) current = i;
    });
    if (current === -1) {
      current = 0;
      items.forEach(function (li, i) {
        if (li.querySelector('.thumbnail[aria-current]')) current = i;
      });
    }
    var step = Number(arrow.getAttribute('data-lcp-step')) || 1;
    var next = items[(current + step + items.length) % items.length];
    var button = next.querySelector('.thumbnail');
    if (button) button.click();
  });

  setupAll();
  afterLoad(function () {
    pageLoaded = true;
    setupAll();
  });
})();
